import { COUNTRY_US } from "@components/Address";
import { TYPE_STRING, TYPE_ZIP, TYPE_COUNTRY } from "@components/Form";

import getRegionsByCountry from "./getRegionsByCountry";
import type { Address, AddressInput, InputForm } from "./types";

type Labels = {
  postalCode: string;
  usPostalCode: string;
  streetAddress: string;
  addressRegion: string;
  addressCountry: string;
  usAddressRegion: string;
  addressLocality: string;
};

type UpdateSchema = () => unknown;

const PLACEHOLDER_STREET = "Street address, apt, suite, unit, building, floor, etc";

const DEFAULT_LABELS: Labels = {
  addressCountry: "Country",
  streetAddress: "Address",
  addressLocality: "City",
  usAddressRegion: "State",
  addressRegion: "Region",
  usPostalCode: "ZIP Code",
  postalCode: "Postal Code",
};

const PLACEHOLDER_ZIP_CODE = "XXXXX";
const PLACEHOLDER_SELECT_STATE = "Select state";

const STREET_MAX_LENGTH = 50;
const LOCALITY_MAX_LENGTH = 32;
const POSTAL_CODE_INPUT_WIDTH = 170;

const getAddressSchema = (
  updateSchema: UpdateSchema,
  addressPath: string[] = [],
  address: Address = {},
  initialAddress: Address = {},
  labels: Partial<Labels> = {},
  isRequired = true,
): AddressInput[] => {
  const mergedLabels: Labels = { ...DEFAULT_LABELS, ...labels };

  const { addressCountry = COUNTRY_US } = address;

  const isUs = addressCountry === COUNTRY_US;

  const options = getRegionsByCountry(addressCountry);

  const regionLabel = isUs
    ? mergedLabels.usAddressRegion
    : mergedLabels.addressRegion;

  const regionPlaceholder = isUs
    ? PLACEHOLDER_SELECT_STATE
    : "";

  const postalCodeLabel = isUs
    ? mergedLabels.usPostalCode
    : mergedLabels.postalCode;

  const postalCodeType = isUs
    ? TYPE_ZIP
    : TYPE_STRING;

  const postalCodePlaceholder = isUs
    ? PLACEHOLDER_ZIP_CODE
    : "";

  const onCountryChange = (_: unknown, form: InputForm): unknown => {
    form.setFieldValue([...addressPath, "addressRegion"], "");

    const result = updateSchema();

    return result;
  };

  const schema: AddressInput[] = [
    {
      name: "addressCountry",
      type: TYPE_COUNTRY,
      label: mergedLabels.addressCountry,
      required: isRequired,
      inputProps: { onChange: onCountryChange },
      initialValue: initialAddress.addressCountry || COUNTRY_US,
    },
    {
      name: "streetAddress",
      label: mergedLabels.streetAddress,
      required: isRequired,
      maxLength: STREET_MAX_LENGTH,
      placeholder: PLACEHOLDER_STREET,
      initialValue: initialAddress.streetAddress,
    },
    {
      name: "addressLocality",
      label: mergedLabels.addressLocality,
      required: isRequired,
      maxLength: LOCALITY_MAX_LENGTH,
      placeholder: "",
      initialValue: initialAddress.addressLocality,
    },
    {
      name: "addressRegion",
      label: regionLabel,
      options,
      required: isRequired,
      placeholder: regionPlaceholder,
      initialValue: initialAddress.addressRegion,
    },
    {
      name: "postalCode",
      type: postalCodeType,
      label: postalCodeLabel,
      required: isRequired,
      inputProps: { style: { width: POSTAL_CODE_INPUT_WIDTH } },
      placeholder: postalCodePlaceholder,
      initialValue: initialAddress.postalCode,
    },
  ];

  return schema;
};

export default getAddressSchema;
